// Imported from @sentry/react, NOT @hyly/observability/react, on purpose. The package's
// `captureException(err, ctx)` wraps the WHOLE context into `{ captureContext: { extra: ctx } }`,
// so a `tags` key lands as an unindexed blob inside Additional Data instead of becoming real
// Sentry tags — the dashboard could not filter/group by api.kind, http.endpoint, etc.
// The raw SDK treats `{ tags, extra }` as a CaptureContext (see @sentry/core captureContextKeys),
// which is what we need. Equivalent at runtime: the package only routes through its own scope
// when `isolatedClient` is set, and this app does not set it.
import { captureException } from "@sentry/react";

// PRODUCTION ONLY: the DSN exists only in config/security.env.production, so dev/qa/stg
// have no DSN -> every function here is a no-op and the API clients behave exactly as
// before. This keeps non-prod byte-for-byte unchanged even though the clients run in all envs.
const enabled = Boolean(process.env.REACT_APP_SENTRY_DSN);

/**
 * Reduce a URL to `host + pathname`, dropping the query string.
 * The query carries api_key / property_id / (sometimes) email — never send it to Sentry.
 * Mirrors the package's own `tagHttpEndpoint` (host+path, no PII, low cardinality).
 */
export const endpointOf = (url) => {
  if (!url) {
    return undefined;
  }

  try {
    const parsed = new URL(url, window.location.origin);

    return `${parsed.host}${parsed.pathname}`;
  } catch (_) {
    return undefined;
  }
};

/**
 * Best-effort GraphQL operation label from the request body we produced.
 * No named operations are used in this app, so extract the first root field (or the
 * operation type) via a light regex — good enough for grouping, never throws.
 */
export const operationFromBody = (body) => {
  if (typeof body !== "string") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(body);
    const query = typeof parsed?.query === "string" ? parsed.query : "";

    // e.g. `query ($orgId: ID!) { privacyPolicyDisclaimer(...) }` -> "privacyPolicyDisclaimer"
    const fieldMatch = query.match(/\b(?:query|mutation|subscription)\b[^{]*\{\s*([A-Za-z_][A-Za-z0-9_]*)/);

    if (fieldMatch) {
      return fieldMatch[1];
    }

    const typeMatch = query.match(/\b(query|mutation|subscription)\b/);

    return typeMatch ? typeMatch[1] : undefined;
  } catch (_) {
    return undefined;
  }
};

/**
 * Capture an API error class that Sentry's httpClientIntegration CANNOT see:
 * network / timeout (no HTTP response) and malformed-JSON-on-200. HTTP-status
 * failures (any response with a status) are deliberately left to the integration
 * so they are never double-captured.
 *
 * Only PII-safe scalars are attached (tags + matching extra) — never request payloads
 * or response bodies (the package scrub only redacts emails/tokens, not names/phones).
 */
export const reportApiError = (error, meta = {}) => {
  if (!enabled || !error) {
    return;
  }

  const { client, kind, endpoint, operation, status } = meta;

  const tags = {};

  if (client) {
    tags["api.client"] = client;
  }

  if (kind) {
    tags["api.kind"] = kind;
  }

  if (endpoint) {
    tags["http.endpoint"] = endpoint;
  }

  if (operation) {
    tags["graphql.operation"] = operation;
  }

  captureException(error, {
    tags,
    extra: { client, kind, endpoint, operation, status },
  });
};

/**
 * Capture a GraphQL response that returned HTTP 200 but carries an `errors[]` payload
 * (invisible to httpClientIntegration because the status is 200). Attaches only each
 * error's message / path / extensions.code and a count — never the `data` body.
 */
export const reportGraphqlErrors = (errors, meta = {}) => {
  if (!enabled || !Array.isArray(errors) || !errors.length) {
    return;
  }

  const { endpoint, operation } = meta;
  const summary = errors[0]?.message || "GraphQL error";
  const error = new Error(`GraphQL errors (${errors.length}): ${summary}`);

  error.name = "GraphQLError";

  const tags = { "api.client": "graphql", "api.kind": "graphql_errors" };

  if (endpoint) {
    tags["http.endpoint"] = endpoint;
  }

  if (operation) {
    tags["graphql.operation"] = operation;
  }

  captureException(error, {
    tags,
    extra: {
      count: errors.length,
      errors: errors.map((e) => ({
        message: e?.message,
        path: e?.path,
        code: e?.extensions?.code,
      })),
    },
  });
};

/**
 * Capture a HANDLED, non-API error that is high-signal for debugging (third-party SDK
 * init, client-side data processing, persisted-state parse) but is caught & degraded
 * gracefully in the UI. These are off the API path, so no interceptor / httpClientIntegration
 * ever sees them.
 *
 * Only PII-safe scalars are attached (`area` / `kind` + a `handled` marker) — never payloads,
 * response bodies, chat text, avatar data URLs, or any user data.
 *
 * `area` / `kind` go out as REAL tags (`error.area` / `error.kind`) so the dashboard can group
 * client-side flow failures the same way it groups API failures.
 */
export const reportHandledError = (error, meta = {}) => {
  if (!enabled || !error) {
    return;
  }

  const { area, kind } = meta;

  const tags = { "error.handled": "true" };

  if (area) {
    tags["error.area"] = area;
  }

  if (kind) {
    tags["error.kind"] = kind;
  }

  captureException(error, {
    tags,
    extra: { handled: true, area, kind },
  });
};
